import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';
import {
    ButtonGroup,
    Button,
    BodyTextMedium,
    HeadingText40,
    HeadingText60,
    pxToRem,
} from '@modernhealth/vital-components';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { navigationService } from 'src/config/rootService';
import type { CoachingVisit } from 'src/modules/videoSessions/rematchModels/videoSessions';
import { convertSessionScheduledTimeToStartAndEndTime } from 'src/modules/videoSessions/helpers/sessionSlotToReadableDate';
import { ProviderAssignmentDisplayType } from 'src/generated/webapp_gql';
import { getConfigForKey } from 'src/shared/helpers/getConfigForKey';
import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';

import { SessionCancellationPromptContent } from './SessionCancellationPromptContent';

interface SessionDetailsModalContentProps {
    session: CoachingVisit;
    onClose: () => void;
}

export const SessionDetails: FC<SessionDetailsModalContentProps> = ({ session, onClose }) => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const [isCancelConfirmationVisible, setCancelConfirmationVisible] = useState(false);

    const handleSessionJoin = () => {
        analytics.trackEvent(
            EventLexicon.clickJoinSession({
                visitUuid: session.uuid,
                providerUuid: session.coachAssignment?.coach?.uuid,
                creationSource: session.creationSource ?? '',
                pageSection: 'For you',
            }),
        );

        navigationService.navigate(`/video-session/${session.uuid}`);
    };

    if (isCancelConfirmationVisible) {
        return (
            <SessionCancellationPromptContent
                session={session}
                onCancel={() => setCancelConfirmationVisible(false)}
                onSuccess={onClose}
                onError={onClose}
            />
        );
    }

    const provider = session.coachAssignment?.coach;
    const sessionTitle = getConfigForKey<ProviderAssignmentDisplayType, string>(
        session.coachAssignment?.providerDisplayType,
        {
            [ProviderAssignmentDisplayType.Therapist]: t(
                'videoSessions:session_details.title.therapy',
                { name: provider?.firstName },
            ),
            [ProviderAssignmentDisplayType.PathwaySpecialist]: t(
                'pathways:session.title_with_provider',
                {
                    name: provider?.firstName,
                },
            ),
            [ProviderAssignmentDisplayType.Psychiatrist]: t(
                `videoSessions:session_details.title.${
                    session.isPsychiatryInitialEvaluation
                        ? 'psychiatry_initial_evaluation'
                        : 'psychiatry_follow_up'
                }`,
                { name: `${provider?.firstName} ${provider?.lastName}` },
            ),
        },
        t('videoSessions:session_details.title.coaching', { name: provider?.firstName }),
    );
    const { scheduledEndTime, scheduledStartTime } =
        convertSessionScheduledTimeToStartAndEndTime(session);

    return (
        <Container>
            <ProviderImageContainer>
                {provider?.avatar ? (
                    <ProviderImage src={provider.avatar} />
                ) : (
                    <HeadingText60 marginBottom={0}>{`${provider?.firstName?.[0] ?? ''}${
                        provider?.lastName?.[0] ?? ''
                    }
                `}</HeadingText60>
                )}
            </ProviderImageContainer>
            <HeadingText40 marginBottom={10} centered>
                {sessionTitle}
            </HeadingText40>
            <BodyTextMedium fontColor="gray65" marginBottom={32} centered>
                <FormattedSessionDateTimeRange
                    start={scheduledStartTime.toDate()}
                    end={scheduledEndTime.toDate()}
                    month="short"
                />
            </BodyTextMedium>

            <ButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.back')}
                </Button>
                <Button variant="primary" onClick={handleSessionJoin}>
                    {t('videoSessions:session_details.join')}
                </Button>
            </ButtonGroup>
            <CancelSessionButton>
                <Button
                    fluid
                    variant="flat"
                    size="large"
                    onClick={() => {
                        analytics.trackEvent(
                            EventLexicon.clickCancelSession({
                                visitUuid: session.uuid,
                                providerUuid: session.coachAssignment?.coach?.uuid,
                                creationSource: session.creationSource ?? '',
                                pageSection: 'For you',
                            }),
                        );
                        setCancelConfirmationVisible(true);
                    }}
                >
                    {t('videoSessions:session_details.cancel')}
                </Button>
            </CancelSessionButton>
        </Container>
    );
};

const Container = styled.div({
    paddingTop: 35,
    display: 'flex',
    flexDirection: 'column',
});

const ProviderImageContainer = styled.div({
    width: pxToRem(98),
    height: pxToRem(98),
    borderRadius: '50%',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    display: 'flex',
    [mediaQuery.maxWidth('md')]: {
        width: pxToRem(40),
        height: pxToRem(40),
    },
    marginBottom: pxToRem(22),
    backgroundColor: dsColors.violet20,
});

const ProviderImage = styled.img({
    width: '110%',
    height: '110%',
});

const CancelSessionButton = styled.div`
    margin-top: ${pxToRem(10)};

    button {
        &,
        &:hover,
        &:focus {
            color: ${dsColors.red40};
        }
    }
`;
